// src/features/curatedLists/types/curatedLists.types.ts

// ===== CONSTANTS =====
export const NOTE_MAX_LENGTH = 280;

// ===== CORE TYPES =====
export interface UserSchema {
  id: number;
  username: string;
}

export interface UrlMetadata {
  title: string | null;
  description: string | null;
  image: string | null;
  content_type: string | null;
  domain: string | null;
  url: string;
  site_name?: string | null;
  favicon?: string | null;
}

export interface PendingListItem {
  tempId: number;
  url: string;
  title: string | null;
  description: string;
  note: string;
  metadata: UrlMetadata | null;
  isExisting?: boolean;
}


// ===== PAGINATION TYPES =====
export interface PaginatedResponse<T> {
  items: T[];
  count: number;
  next: string | null;
  previous: string | null;
  page: number;
  page_size: number;
}

// Specific paginated response for lists
export type PaginatedCuratedLists = PaginatedResponse<CuratedList>;

// ===== LIST ITEM TYPES =====
export interface ListItemBase {
  id: number;
  url: string;
  title: string | null;
  content_type: string;
  position: number;
  added_at: string;
  updated_at: string;
  added_by: UserSchema;
}

export interface ListItem extends ListItemBase {
  thumbnail_url: string | null;
  description: string;
  note: string;
  domain: string | null;
  
  // App content fields
  app_url?: string | null;
  app_content_type?: string | null;
  app_content_id?: number | null;

  // Parent blog info
  parent_blog_id?: number | null;
  parent_blog_slug?: string | null;

  // Analytics
  view_count?: number;
}

// Computed properties type
export type ListItemWithComputed = ListItem & {
  display_url?: string;
  is_from_app?: boolean;
};

export type ListItemWithExisting = ListItem & { isExisting?: boolean };


// ===== LIST TYPES =====
export interface CuratedListBase {
  id: number;
  title: string;
  slug: string;
  creator: UserSchema;
  created_at: string;
  updated_at: string;
  absolute_url: string;
  total_bookmarks: number;
  total_items: number;
  is_featured: boolean;
  featured_at: string | null;
  cover_image: string | null;
  pin_weight?: number;
  pinned_until?: string | null;
}

export interface CuratedList extends CuratedListBase {
  description: string;
  items: ListItem[];
  is_bookmarked: boolean;
  
  // Permission fields
  is_owner: boolean;
  can_edit: boolean;
  can_delete: boolean;
  is_unlisted: boolean;
  
  // Analytics
  view_count?: number;
}

export interface CuratedListSimple extends CuratedListBase {
  description: string | null;
  is_owner?: boolean;
  can_edit?: boolean;
  view_count: number;
}

// ===== REQUEST TYPES =====
// Standardize on "Request" naming (matches your actions file)
export interface CreateListRequest {
  title: string;
  description?: string;
}

export interface AddItemRequest {
  url: string;
  title?: string;
  note?: string;
  position?: number;
}

export interface UpdateListRequest {
  title?: string;
  description?: string;
}

export interface AddAppContentToListRequest {
  app_content_type: 'blog' | 'blog_post' | 'featured_article';
  app_content_id: number;
  list_id?: number | null;
  list_title?: string | null;
  note?: string | null;
}

// ===== RESPONSE TYPES =====
export interface AddAppContentToListResponse {
  id: number;
  url: string;
  app_url: string;
  title: string;
  position: number;
  added_at: string;
  curated_list: {
    id: number;
    title: string;
    absolute_url: string;
  };
}

export interface BookmarkToggleResponse {
  is_bookmarked: boolean;
  total_bookmarks: number;
}

// ===== ERROR TYPES =====
export interface ApiError {
  detail?: string;
  message?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

// ===== STATE TYPES =====
export interface CuratedListsState {
  // Current list
  currentList: CuratedList | null;
  
  // List creation/editing
  pendingItems: PendingListItem[];
  listTitle: string;
  
  // User's lists
  userLists: CuratedListSimple[];
  
  // Featured lists
  featuredLists: CuratedList[];
  featuredListsLastFetched: number | null;
  paginatedFeaturedLists: CuratedList[];
  featuredCurrentPage: number;
  featuredTotalCount: number;
  featuredHasMore: boolean;
  isFetchingFeaturedLists: boolean;
  isFetchingPaginatedFeatured: boolean;
  featuredError: string | null;

  // Loading states
  isCreating: boolean;
  isFetchingMetadata: boolean;
  isLoading: boolean;
  isAddingContent: boolean;
  isFetchingUserLists: boolean;
  
  // Messages
  error: string | null;
  successMessage: string | null;
  lastAddedItem: AddAppContentToListResponse | null;

  browseLists: CuratedList[];
  browseCurrentPage: number;
  browseTotalCount: number;
  browseHasMore: boolean;
  isFetchingBrowseLists: boolean;
  browseError: string | null;
}

// ===== HELPER TYPES & CONSTANTS =====
export const APP_CONTENT_TYPES = ['blog', 'blog_post', 'featured_article'] as const;
export type AppContentType = typeof APP_CONTENT_TYPES[number];

// ===== TYPE GUARDS =====
export function validateAppContentType(type: string): type is AppContentType {
  return APP_CONTENT_TYPES.includes(type as AppContentType);
}

export function isListItemFromApp(item: ListItem): boolean {
  return !!(item.app_content_type && item.app_content_id);
}

// ===== HELPER FUNCTIONS =====
export function getListItemDisplayUrl(item: ListItem): string {
  return item.app_url || item.url;
}

export function withComputedProperties(item: ListItem): ListItemWithComputed {
  return {
    ...item,
    display_url: getListItemDisplayUrl(item),
    is_from_app: isListItemFromApp(item),
  };
}



export interface CuratedListItemProps {
  item: ListItem;
  onRemove?: (id: number) => void;
  isRemoving?: boolean;
  isDraggable?: boolean;
}

export interface CuratedListDisplayProps {
  list: CuratedList;
  onItemRemove?: (itemId: number) => void;
  onReorder?: (itemIds: number[]) => void;
  isDraggable?: boolean;
}

export interface AddItemFormProps {
  onSubmit: (url: string, title?: string, note?: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export interface CuratedListComponentProps {
  items: ListItemWithExisting[];
  onRemove?: (id: string | number) => void;
  removingItemId?: string | number | null;
  onReorder?: (itemIds: (string | number)[]) => void;
  isDraggable?: boolean;
  emptyMessage?: string;
  isLoading?: boolean; 
  error?: string | null;
}



export interface ToggleFeaturedRequest {
  list_id: number;
  is_featured: boolean;
}